import { useMemo, useState } from 'react';
import type { ColDef } from 'ag-grid-community';
import { NJButton, NJCheckbox, NJFormItem, NJInlineMessage, NJText, NJToggle } from '@engie-group/fluid-design-system-react';
import type { ICellRendererParams } from 'ag-grid-community';
import DataTable from '@shared/DataTable';
import { EditableRowTable } from './EditableRows';
import { ENR_RATE_NAME_MAX, POLLUTANT_NAME_MAX } from './fixtures';
import type { EnrRate, EnrRateInput, FuelContract, Pollutant, PollutantInput, VariablePrice } from './types';
import { CreationInfo, formatValue, inputValue, MandatoryNotice, parseNonNegative, toNumberOrNull, RightSidePanel } from './ui';

type ContractValueRow = { id: string; contract: string; value: number | null };

function ContractValuesTable({ testId, valueHeader, rows, onChange, max, format = formatValue }: {
    testId: string;
    valueHeader: string;
    rows: ContractValueRow[];
    onChange: (id: string, value: number | null) => void;
    max?: number;
    format?: (value: number | null | undefined) => string;
}) {
    const header = useMemo<ColDef<ContractValueRow>[]>(() => [
        { field: 'contract', headerName: 'Contrat' },
        {
            field: 'value',
            headerName: valueHeader,
            editable: true,
            cellClass: 'production-contracts-editable production-contracts-center',
            valueParser: (params) => {
                const parsed = parseNonNegative(params);
                return typeof parsed === 'number' && max !== undefined ? Math.min(max, parsed) : parsed;
            },
            valueFormatter: ({ value }) => format(value),
        },
    ], [valueHeader, max, format]);
    return (
        <div className="production-contracts-inline-table">
            <DataTable<ContractValueRow>
                testId={testId}
                header={header}
                data={rows}
                defaultColDef={{ flex: 1 }}
                onCellValueChanged={(event) => onChange(event.data.id, event.newValue ?? null)}
            />
        </div>
    );
}

const numberText = (value: number | null | undefined) => (value === null || value === undefined ? '' : String(value));
const isBad = (text: string) => { const n = toNumberOrNull(text); return n !== null && (Number.isNaN(n) || n < 0); };

function PanelFooter({ formId, label, disabled, onClose }: { formId: string; label: string; disabled: boolean; onClose: () => void }) {
    return (
        <>
            <NJButton label="Annuler" variant="secondary" onClick={onClose} />
            <NJButton label={label} type="submit" form={formId} disabled={disabled} />
        </>
    );
}

/** Nemo `MIN_MAX` messages of `EnergyMixBaseForm`. */
function rangeError(text: string, min: number, max: number, required = false): string | undefined {
    const value = toNumberOrNull(text);
    if (value === null) return required ? 'Ce champ est obligatoire' : undefined;
    if (Number.isNaN(value) || value < min) return `Ce champ doit être un nombre supérieur ou égal à ${min}`;
    if (value > max) return `Ce champ doit être un nombre inférieur ou égal à ${max}`;
    return undefined;
}

const percent = (value: number | null | undefined) => (value === null || value === undefined ? '' : `${Number(Number(value).toFixed(2))}%`);
const DATE_FIELDS = [
    { key: 'startDay', label: 'Jour de la date de début', max: 31 },
    { key: 'startMonth', label: 'Mois de la date de début', max: 12 },
    { key: 'endDay', label: 'Jour de la date de fin', max: 31 },
    { key: 'endMonth', label: 'Mois de la date de fin', max: 12 },
] as const;
type DateKey = (typeof DATE_FIELDS)[number]['key'];

/** Nemo `EnergyMixCreateForm` / `EnergyMixEditForm` (shared `EnergyMixBaseForm`). */
export function EnrRatePanel({ rate, contracts, existingNames, onClose, onSubmit, error, isPending }: {
    rate?: EnrRate;
    contracts: FuelContract[];
    existingNames: string[];
    onClose: () => void;
    onSubmit: (value: EnrRateInput) => void;
    error?: string;
    isPending?: boolean;
}) {
    const initial = useMemo(() => ({
        name: rate?.name ?? '',
        shares: Object.fromEntries(contracts.map((c) => [c.id, rate?.shares[c.id] ?? 0])) as Record<string, number | null>,
        min: numberText(rate?.min),
        max: numberText(rate?.max),
        dates: Object.fromEntries(DATE_FIELDS.map(({ key }) => [key, String(rate?.[key] ?? 1)])) as Record<DateKey, string>,
    }), [rate, contracts]);
    const [name, setName] = useState(initial.name);
    const [shares, setShares] = useState(initial.shares);
    const [min, setMin] = useState(initial.min);
    const [max, setMax] = useState(initial.max);
    const [dates, setDates] = useState(initial.dates);
    const [advanced, setAdvanced] = useState(false);

    const trimmed = name.trim();
    const duplicate = existingNames.some((existing) => existing.toLowerCase() === trimmed.toLowerCase() && existing !== rate?.name);
    const minError = rangeError(min, 0, 100);
    const maxError = rangeError(max, 0, 100);
    const dateErrors = Object.fromEntries(DATE_FIELDS.map(({ key, max: upper }) => [key, rangeError(dates[key], 1, upper, true)])) as Record<DateKey, string | undefined>;
    // Nemo edit form: « Valider » stays disabled until something changed (`requireChangesToSubmit`).
    const dirty = JSON.stringify({ name, shares, min, max, dates }) !== JSON.stringify(initial);
    const canSubmit = !!trimmed && !duplicate && !minError && !maxError && !Object.values(dateErrors).some(Boolean) && (!rate || dirty) && !isPending;

    const submit = (event: React.FormEvent) => {
        event.preventDefault();
        if (!canSubmit) return;
        onSubmit({
            name: trimmed,
            shares: Object.fromEntries(Object.entries(shares).map(([id, value]) => [id, value ?? 0])),
            min: toNumberOrNull(min),
            max: toNumberOrNull(max),
            startDay: Number(dates.startDay),
            startMonth: Number(dates.startMonth),
            endDay: Number(dates.endDay),
            endMonth: Number(dates.endMonth),
        });
    };

    return (
        <RightSidePanel
            isOpen
            onClose={onClose}
            title={rate ? rate.name : 'Créer un taux EnR'}
            testId="enr-rate-panel"
            footer={<PanelFooter formId="enr-rate-form" label="Valider" disabled={!canSubmit} onClose={onClose} />}
        >
            <form id="enr-rate-form" className="production-contracts-form production-contracts-panel-form" onSubmit={submit} noValidate>
                {error && <NJInlineMessage variant="error">{error}</NJInlineMessage>}
                <MandatoryNotice />
                <NJText scale="sm"><strong>Contribution des contrats combustibles aux taux EnR</strong></NJText>
                <NJFormItem
                    id="enr-rate-name"
                    label="Nom"
                    isRequired
                    value={name}
                    maxLength={ENR_RATE_NAME_MAX}
                    onChange={(event) => setName(inputValue(event))}
                    hasError={duplicate}
                    subscriptMessage={duplicate ? 'Un taux EnR portant ce nom existe déjà.' : `${name.length}/${ENR_RATE_NAME_MAX} caractères`}
                />
                <NJText scale="sm"><strong>Répartition des contrats dans ce taux EnR *</strong></NJText>
                <ContractValuesTable
                    testId="enr-rate-shares"
                    valueHeader={trimmed || 'Taux EnR'}
                    max={100}
                    format={percent}
                    rows={contracts.map((c) => ({ id: c.id, contract: c.name, value: shares[c.id] ?? 0 }))}
                    onChange={(id, value) => setShares((current) => ({ ...current, [id]: value }))}
                />
                <NJText scale="sm"><strong>Seuils des taux EnR</strong></NJText>
                <NJFormItem id="enr-rate-min" label="Minimum (%)" type="number" value={min} onChange={(event) => setMin(inputValue(event))} hasError={!!minError} subscriptMessage={minError} />
                <NJFormItem id="enr-rate-max" label="Maximum (%)" type="number" value={max} onChange={(event) => setMax(inputValue(event))} hasError={!!maxError} subscriptMessage={maxError} />
                <NJToggle id="enr-rate-advanced" label="Afficher les options avancées" checked={advanced} onChange={(_event, checked) => setAdvanced(checked)} />
                {advanced && DATE_FIELDS.map(({ key, label }) => (
                    <NJFormItem
                        key={key}
                        id={`enr-rate-${key}`}
                        label={label}
                        isRequired
                        type="number"
                        value={dates[key]}
                        onChange={(event) => setDates((current) => ({ ...current, [key]: inputValue(event) }))}
                        hasError={!!dateErrors[key]}
                        subscriptMessage={dateErrors[key]}
                    />
                ))}
                {rate && <CreationInfo data={rate} italic />}
            </form>
        </RightSidePanel>
    );
}

type PollutantPriceRow = { key: number; date: string; price: number | null };
let priceKey = 0;
const toPriceRow = (price: VariablePrice): PollutantPriceRow => ({ key: ++priceKey, date: price.date, price: price.price });
/** Nemo pollutant default variable prices when none exist. */
const DEFAULT_POLLUTANT_PRICES: VariablePrice[] = [{ date: '1900-01', price: 0 }, { date: '3000-12', price: 0 }];
const MONTH_REGEX = /^\d{4}-(0[1-9]|1[0-2])$/;
const BOUND_DATE_REGEX = /^(0[1-9]|[12]\d|3[01])-(0[1-9]|1[0-2])$/;
const POLLUTANT_PRICE_COLUMNS: ColDef<PollutantPriceRow>[] = [
    {
        field: 'date',
        headerName: 'Date',
        editable: true,
        valueParser: ({ newValue }) => String(newValue ?? '').trim(),
        cellRenderer: ({ value }: ICellRendererParams<PollutantPriceRow, string>) => (
            <span className={`production-contracts-ellipsis${value && !MONTH_REGEX.test(value) ? ' production-contracts-invalid' : ''}`} title={value ?? ''}>{value ?? ''}</span>
        ),
    },
    {
        field: 'price',
        headerName: 'Prix (€/kg)',
        editable: true,
        cellEditor: 'agNumberCellEditor',
        valueParser: ({ newValue, oldValue }) => { const parsed = Number(newValue); return Number.isNaN(parsed) ? oldValue : Math.max(0, parsed); },
        valueFormatter: ({ value }) => (value === null || value === undefined ? '' : Number(value).toFixed(2)),
    },
];
const boundValue = ({ day, month }: { day: number; month: number }) => month * 100 + day;
const parseBound = (text: string) => { const [day, month] = text.split('-').map(Number); return { day, month }; };

/** Nemo `PollutantForm` (create « Créer un nouveau polluant », edit: pollutant name). */
export function PollutantPanel({ pollutant, contracts, existingNames, onClose, onSubmit, error, isPending }: {
    pollutant?: Pollutant;
    contracts: FuelContract[];
    existingNames: string[];
    onClose: () => void;
    onSubmit: (value: Partial<PollutantInput> & Pick<PollutantInput, 'name'>) => void;
    error?: string;
    isPending?: boolean;
}) {
    const hasBound = pollutant?.maxThreshold !== null && pollutant?.maxThreshold !== undefined;
    const [name, setName] = useState(pollutant?.name ?? '');
    const [emissions, setEmissions] = useState<Record<string, number | null>>(() => Object.fromEntries(contracts.map((c) => [c.id, pollutant?.emissions[c.id] ?? 0])));
    const [fixedPrice, setFixedPrice] = useState(numberText(pollutant?.fixedPrice ?? 0));
    const [prices, setPrices] = useState<PollutantPriceRow[]>(() => (pollutant?.variablePrices.length ? pollutant.variablePrices : DEFAULT_POLLUTANT_PRICES).map(toPriceRow));
    const [advanced, setAdvanced] = useState(hasBound);
    const [withBound, setWithBound] = useState(hasBound);
    const [threshold, setThreshold] = useState(numberText(pollutant?.maxThreshold));
    const [boundStart, setBoundStart] = useState(pollutant?.boundStart ?? '');
    const [boundEnd, setBoundEnd] = useState(pollutant?.boundEnd ?? '');

    const trimmed = name.trim();
    const duplicate = existingNames.some((existing) => existing.toLowerCase() === trimmed.toLowerCase() && existing !== pollutant?.name);
    const fixedError = isBad(fixedPrice) ? 'Le prix fixe doit être un nombre positif' : undefined;
    const invalidMonths = prices.some((row) => !!row.date && !MONTH_REGEX.test(row.date));
    const thresholdError = !withBound ? undefined
        : toNumberOrNull(threshold) === null ? 'Le seuil maximal est obligatoire'
            : isBad(threshold) ? 'Le seuil maximal doit être un nombre positif' : undefined;
    const startError = !withBound ? undefined
        : !boundStart ? 'Les dates de borne sont obligatoires lors de l\'ajout d\'une borne'
            : !BOUND_DATE_REGEX.test(boundStart) ? 'La date de début doit être au format JJ-MM' : undefined;
    const endError = !withBound ? undefined
        : !boundEnd ? 'Les dates de borne sont obligatoires lors de l\'ajout d\'une borne'
            : !BOUND_DATE_REGEX.test(boundEnd) ? 'La date de fin doit être au format JJ-MM'
                : !startError && boundValue(parseBound(boundEnd)) < boundValue(parseBound(boundStart)) ? 'La date de fin doit être supérieure ou égale à la date de début' : undefined;
    const canSubmit = !!trimmed && !duplicate && toNumberOrNull(fixedPrice) !== null && !fixedError && !invalidMonths && !thresholdError && !startError && !endError && !isPending;

    const submit = (event: React.FormEvent) => {
        event.preventDefault();
        if (!canSubmit) return;
        const value: Partial<PollutantInput> & Pick<PollutantInput, 'name'> = {
            name: trimmed,
            emissions,
            fixedPrice: toNumberOrNull(fixedPrice),
            variablePrices: prices.filter((row) => row.date).map(({ date, price }) => ({ date, price: price ?? 0 })),
        };
        // Nemo: the bound is only (re)written when the advanced options are displayed.
        if (!pollutant || advanced) {
            const bound = advanced && withBound;
            Object.assign(value, { maxThreshold: bound ? toNumberOrNull(threshold) : null, boundStart: bound ? boundStart : null, boundEnd: bound ? boundEnd : null });
        }
        onSubmit(value);
    };

    return (
        <RightSidePanel
            isOpen
            onClose={onClose}
            title={pollutant ? pollutant.name : 'Créer un nouveau polluant'}
            testId="pollutant-panel"
            footer={<PanelFooter formId="pollutant-form" label={pollutant ? 'Modifier' : 'Valider'} disabled={!canSubmit} onClose={onClose} />}
        >
            <form id="pollutant-form" className="production-contracts-form production-contracts-panel-form" onSubmit={submit} noValidate>
                {error && <NJInlineMessage variant="error">{error}</NJInlineMessage>}
                <MandatoryNotice />
                <NJFormItem
                    id="pollutant-name"
                    label="Nom"
                    isRequired
                    value={name}
                    maxLength={POLLUTANT_NAME_MAX}
                    onChange={(event) => setName(inputValue(event))}
                    hasError={duplicate}
                    subscriptMessage={duplicate ? 'Un polluant portant ce nom existe déjà' : `${name.length}/${POLLUTANT_NAME_MAX} caractères`}
                />
                {contracts.length > 0 && (
                    <>
                        <NJText scale="sm"><strong>Facteurs d'émission par contrat *</strong></NJText>
                        <ContractValuesTable
                            testId="pollutant-emissions"
                            valueHeader={`${trimmed || 'Facteur d\'émission'} (kg)`}
                            format={(value) => (value === null || value === undefined ? '' : String(Number(value)))}
                            rows={contracts.map((c) => ({ id: c.id, contract: c.name, value: emissions[c.id] ?? 0 }))}
                            onChange={(id, value) => setEmissions((current) => ({ ...current, [id]: value ?? 0 }))}
                        />
                    </>
                )}
                <NJFormItem
                    id="pollutant-fixed-price"
                    label="Prix fixe (€/quantité)"
                    isRequired
                    type="number"
                    min={0}
                    step={1}
                    value={fixedPrice}
                    // Nemo rounds the fixed price to an integer while typing.
                    onChange={(event) => { const raw = inputValue(event); setFixedPrice(raw === '' ? '0' : String(Math.round(Number(raw)))); }}
                    hasError={!!fixedError}
                    subscriptMessage={fixedError}
                />
                <div className="production-contracts-subsection">
                    <NJText scale="sm"><strong>Prix variable (mensuel)</strong></NJText>
                    <EditableRowTable<PollutantPriceRow>
                        testId="pollutant-prices-table"
                        columns={POLLUTANT_PRICE_COLUMNS}
                        rows={prices}
                        onChange={setPrices}
                        createEmptyRow={() => ({ key: ++priceKey, date: '', price: 0 })}
                    />
                    {invalidMonths && <NJInlineMessage variant="error">Le format de date doit être AAAA-MM</NJInlineMessage>}
                </div>
                <NJToggle id="pollutant-advanced" label="Afficher les options avancées" checked={advanced} onChange={(_event, checked) => setAdvanced(checked)} />
                {advanced && (
                    <>
                        <NJCheckbox inputId="pollutant-bound-enabled" label="Ajouter une borne" isChecked={withBound} onChange={(_event, checked) => setWithBound(!!checked)} />
                        {withBound && (
                            <>
                                <NJFormItem id="pollutant-threshold" label="Seuil maximal (kg)*" type="number" min={0} step={0.01} value={threshold} onChange={(event) => setThreshold(inputValue(event))} hasError={!!thresholdError} subscriptMessage={thresholdError} />
                                <NJFormItem id="pollutant-bound-start" label="Date de début (JJ-MM)" placeholder="JJ-MM" value={boundStart} onChange={(event) => setBoundStart(inputValue(event))} hasError={!!startError} subscriptMessage={startError} />
                                <NJFormItem id="pollutant-bound-end" label="Date de fin (JJ-MM)" placeholder="JJ-MM" value={boundEnd} onChange={(event) => setBoundEnd(inputValue(event))} hasError={!!endError} subscriptMessage={endError} />
                            </>
                        )}
                    </>
                )}
                {pollutant && <CreationInfo data={pollutant} />}
            </form>
        </RightSidePanel>
    );
}
